import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Calendar } from 'lucide-react';
import allPosts from '../data/blogs.json';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const post = allPosts.find(p => p.slug === slug);

  useEffect(() => {
    if (post) {
      setLoading(true);
      // Remove frontmatter from content before rendering
      fetch(`/content/${post.slug}.md`)
        .then(res => {
            if (!res.ok) {
                throw new Error(`Failed to fetch post: ${res.status} ${res.statusText}`);
            }
            return res.text();
        })
        .then(text => {
            const body = text.replace(/^---[\s\S]*?---/, '');
            setContent(body);
            setLoading(false);
        })
        .catch(err => {
            console.error('Blog fetch error:', err);
            setError(true);
            setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-primary flex flex-col">
        <div className="pt-16 flex-grow flex items-center justify-center">
          <div className="text-center max-w-2xl mx-auto px-6">
            <h1 className="text-4xl font-light text-text mb-8">Post Not Found</h1>
            <p className="text-muted mb-8">
              The blog post you're looking for doesn't exist or has been moved.
            </p>
            <button
              onClick={() => navigate('/blog')}
              className="px-6 py-3 bg-secondary text-gray-900 rounded-lg hover:bg-cyan-400 transition-colors duration-200"
            >
              ← Back to All Posts
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-primary flex flex-col">
        <div className="pt-16 flex-grow flex items-center justify-center">
          <div className="text-center max-w-2xl mx-auto px-6">
            <h1 className="text-4xl font-light text-text mb-8">Error Loading Post</h1>
            <p className="text-muted mb-8">
              There was an error loading this blog post. Please try again later.
            </p>
            <button
              onClick={() => navigate('/blog')}
              className="px-6 py-3 bg-secondary text-gray-900 rounded-lg hover:bg-cyan-400 transition-colors duration-200"
            >
              ← Back to All Posts
            </button>
          </div>
        </div>
      </div>
    );
  }

  const markdownComponents = {
    h1: ({ children, ...props }: any) => (
      <h1 className="text-3xl lg:text-4xl font-light text-text mb-6 mt-8" {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }: any) => (
      <h2 className="text-2xl lg:text-3xl font-light text-text mb-6 mt-8" {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }: any) => (
      <h3 className="text-xl lg:text-2xl font-light text-text mb-6 mt-6" {...props}>
        {children}
      </h3>
    ),
    p: (props: any) => <p className="mb-6 text-muted leading-relaxed" {...props} />,
    ul: (props: any) => <ul className="mb-6 pl-6 space-y-2 list-disc text-muted" {...props} />,
    li: (props: any) => <li className="mb-2" {...props} />,
    strong: (props: any) => <strong className="text-secondary font-semibold" {...props} />,
    a: (props: any) => <a className="text-secondary hover:text-cyan-400 underline font-medium transition-colors" {...props} />,
  };

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <div className="pt-16 flex-grow">
        <article className="pt-20 pb-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-muted hover:text-secondary transition-colors duration-200 mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to All Posts
            </button>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary mx-auto mb-4"></div>
                <p className="text-muted">Loading post...</p>
              </div>
            ) : (
              <>
                <header className="mb-12 border-b border-border pb-8">
                  <div className="flex items-center gap-4 mb-6 text-sm text-muted">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    {post.category && (
                      <span className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs uppercase tracking-wider">
                        {post.category}
                      </span>
                    )}
                  </div>
                  
                  <h1 className="text-3xl lg:text-5xl font-light text-text mb-6 leading-tight">
                    {post.title}
                  </h1>
                  
                  {post.excerpt && (
                    <p className="text-xl text-muted font-light leading-relaxed">
                      {post.excerpt}
                    </p>
                  )}
                </header>

                <div className="prose prose-lg max-w-none">
                  <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
                </div>
              </>
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
